import { useMemo } from 'react';
import styled from 'styled-components';
import { ISearchInput } from '../types';
import { getAdvancedSearchConfig } from '../SearchResultItem/utils';
import TagSearchOption from './TagSearchOption';
import LastUpdatedSearchOption from './LastUpdatedSearchOption';
import CreatorSearchOption from './CreatorSearchOption';
import QueryTypeSearchOption from './QueryTypeSearchOption';
import SchemaSearchOption from './SchemaSearchOption';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import {
  PermissionKey,
  QueryType,
  SearchEntityType,
} from '../../../../utils/apollo/graphqlSchema';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import { assertNotNil } from '../../../../utils/types';
import ShowFailedReportsOption from './ShowFailedReportOption';
import { theme } from '../../../../styles/theme';
import ScrollableWrapper from '../../../../components/ScrollableWrapper';
import { SEARCH_MENU_FILTERS_WIDTH } from '../utils';

interface IProps {
  searchInput: ISearchInput;
  setSearchInput: (searchInput: ISearchInput, isLoadMore: boolean) => void;
  supportedQueryTypes: QueryType[];
  onReset: () => void;
}

const Container = styled.div`
  flex: 0 0 auto;
  width: ${SEARCH_MENU_FILTERS_WIDTH}px;
  border-left: 1px solid ${theme.colors.border};
`;

const Wrapper = styled(ScrollableWrapper)`
  row-gap: 16px;
  padding: 8px 16px;
`;

const AdvancedSearchMenu = ({
  searchInput,
  setSearchInput,
  supportedQueryTypes,
  onReset,
}: IProps) => {
  const { checkIsPermitted } = usePermission();
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);

  // This is the special case for universal search with category filter.
  // if no schema is selected, check if the user has permission to view categories in all schemas
  // if there are specific schemas selected, check if the user has permission to view categories in each of them
  const isPermittedToViewCategories = useMemo(() => {
    if (searchInput.schemaIds?.length === 0) {
      return checkIsPermitted({
        requiredPermissionKeys: [PermissionKey.CategoryRead],
        schemaId: permissionConfig.allSchema.id,
      });
    } else {
      return searchInput.schemaIds.every((schemaId) =>
        checkIsPermitted({
          requiredPermissionKeys: [PermissionKey.CategoryRead],
          schemaId,
        }),
      );
    }
  }, [searchInput.schemaIds, checkIsPermitted, permissionConfig.allSchema.id]);

  const advancedSearchConfig = getAdvancedSearchConfig(searchInput.types);
  const showFailedReportsToggle =
    searchInput.types?.length === 0 ||
    searchInput.types?.includes(SearchEntityType.Analysis);

  return (
    <Container>
      <Wrapper>
        <LastUpdatedSearchOption
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          onReset={onReset}
        />
        {advancedSearchConfig.schemaIds && (
          <SchemaSearchOption
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />
        )}
        {advancedSearchConfig.queryTypes && (
          <QueryTypeSearchOption
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            supportedQueryTypes={supportedQueryTypes}
          />
        )}
        {advancedSearchConfig.editorIds && (
          <CreatorSearchOption
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />
        )}
        {advancedSearchConfig.categoryIds && isPermittedToViewCategories && (
          <TagSearchOption
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />
        )}
        {showFailedReportsToggle && (
          <ShowFailedReportsOption
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />
        )}
      </Wrapper>
    </Container>
  );
};

export default AdvancedSearchMenu;
